import { message } from "antd";
import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from "mobx";
import { ErrorStore } from "../../common/stores";
import { ActionService } from "../services/ActionService";
import { GlobalAdministrationService } from "../services/GlobalAdministrationService";
import {
    ActionConstraint,
    ActionDefinition,
    ActionExecutionPermissions,
    ActionField,
    ActionRole,
    AiLookupItem,
    ActionType,
    EditorConfig,
    PayloadStructure,
} from "../types/Actions";
import { MetadataDefinition } from "../types/Metadata";
import TasksRootStore from "src/modules/tasks/stores/TasksRootStore";

export type ActionRoleSummary = {
    id: string;
    name: string;
    description?: string;
    usedInActions: number;
};

export default class ActionDefinitionStore {
    actionTypes: ActionType[] = [];
    actionDefinitions: ActionDefinition[] = [];
    metadata: MetadataDefinition[] = [];
    aiProjects: AiLookupItem[] = [];
    aiApplications: AiLookupItem[] = [];
    editDialogMode: "create" | "edit" = "create";
    editDialogVisible: boolean = false;
    editInputDialogVisible: boolean = false;
    editPayloadDialogVisible: boolean = false;
    selectedDefinition: ActionDefinition | null;
    selectedProjectId: string;
    selectedEditorConfig?: EditorConfig;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editorConfigChangeCallback?: (values: any) => void;
    selectedPayloadStructure: PayloadStructure | null;
    payloadStructureChangeCallback?: (values: PayloadStructure | null) => void;
    payloadStructureFields?: string[];
    isLoading: boolean = false;
    aiProjectsLoading: boolean = false;
    aiApplicationsLoading: boolean = false;
    aiProjectsLoaded: boolean = false;
    aiApplicationsProjectId?: string;
    searchString: string = "";
    actionRolesDialogVisible: boolean = false;
    actionRolesLoading: boolean = false;
    actionRoles: ActionRole[] = [];

    constructor(
        private adminService: GlobalAdministrationService,
        private actionService: ActionService,
        private taskRootStore: TasksRootStore,
        private errorStore: ErrorStore,
    ) {
        makeObservable<ActionDefinitionStore>(this, {
            actionTypes: observable,
            actionDefinitions: observable,
            metadata: observable,
            aiProjects: observable,
            aiApplications: observable,
            editDialogMode: observable,
            editDialogVisible: observable,
            editInputDialogVisible: observable,
            editPayloadDialogVisible: observable,
            selectedDefinition: observable,
            selectedProjectId: observable,
            selectedEditorConfig: observable,
            editorConfigChangeCallback: observable,
            searchString: observable,
            selectedPayloadStructure: observable,
            payloadStructureChangeCallback: observable,
            payloadStructureFields: observable,
            isLoading: observable,
            aiProjectsLoading: observable,
            aiApplicationsLoading: observable,
            aiProjectsLoaded: observable,
            aiApplicationsProjectId: observable,
            actionRolesDialogVisible: observable,
            actionRolesLoading: observable,
            actionRoles: observable,
            canSaveActionDefinition: computed,
            actionRoleSummaries: computed,
            availableActionRoles: computed,
            actionRoleOptions: computed,
            setNewDefinitionDialogVisible: action.bound,
            setEditInputDialogVisible: action.bound,
            setEditPayloadDialogVisible: action.bound,
            setSelectedProjectId: action.bound,
            loadActionTypes: action.bound,
            loadDefinitions: action.bound,
            selectActionDefinition: action.bound,
            createActionDefinition: action.bound,
            deleteActionDefinition: action.bound,
            updateActionDefinition: action.bound,
            setIsLoading: action,
            setAiProjectsLoading: action.bound,
            setAiApplicationsLoading: action.bound,
            clearAiApplications: action.bound,
            setSearchString: action,
            filteredActionDefinitions: computed,
            currentActionType: computed,
            ensureAiProjectsLoaded: action.bound,
            loadAiApplications: action.bound,
            setActionRolesDialogVisible: action.bound,
            loadActionRoles: action.bound,
        });
    }

    get canSaveActionDefinition() {
        return true;
    }

    get filteredActionDefinitions() {
        if (this.searchString.trim() !== "") {
            const searchStringLower = this.searchString.toLowerCase();
            return this.actionDefinitions.filter(
                (a) =>
                    a.name.toLowerCase().includes(searchStringLower) ||
                    a.title?.toLowerCase().includes(searchStringLower),
            );
        }
        return this.actionDefinitions;
    }

    get currentActionType() {
        return this.actionTypes.find(
            (t) => t.name === this.selectedDefinition?.actionType,
        );
    }

    get availableActionRoles() {
        return this.actionRoles
            .map((role) => role.name)
            .sort((a, b) => a.localeCompare(b));
    }

    get actionRoleOptions() {
        return this.actionRoles
            .map((role) => ({
                label: role.name,
                value: role.id,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    getActionRoleName(roleIdOrName: string) {
        return (
            this.actionRoles.find(
                (role) =>
                    role.id === roleIdOrName || role.name === roleIdOrName,
            )?.name ?? roleIdOrName
        );
    }

    get actionRoleSummaries(): ActionRoleSummary[] {
        return this.actionRoles
            .map((role) => ({
                id: role.id,
                name: role.name,
                description: role.description,
                usedInActions: this.actionDefinitions.filter((definition) =>
                    (definition.permissions?.roles ?? []).some(
                        (roleIdOrName) =>
                            roleIdOrName === role.id ||
                            roleIdOrName === role.name,
                    ),
                ).length,
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }

    setIsLoading(val: boolean) {
        this.isLoading = val;
    }

    setAiProjectsLoading(val: boolean) {
        this.aiProjectsLoading = val;
    }

    setAiApplicationsLoading(val: boolean) {
        this.aiApplicationsLoading = val;
    }

    clearAiApplications() {
        this.aiApplications = [];
        this.aiApplicationsProjectId = undefined;
        this.aiApplicationsLoading = false;
    }

    async setSelectedProjectId(projectId: string) {
        this.selectedProjectId = projectId;
        this.isLoading = true;
        this.actionDefinitions = [];
        this.actionRoles = [];
        this.loadDefinitions(projectId);
        this.loadActionRoles(projectId);
        const resp =
            await this.adminService.getMetadataDefinitionsForProject(projectId);
        resp.map((metadata) => {
            runInAction(() => {
                this.metadata = metadata;
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }).mapErr((err: any) => this.errorStore.addError(err.data));
    }

    selectActionDefinition(definition: ActionDefinition | null) {
        this.selectedDefinition = definition;
        if (!definition) {
            this.clearAiApplications();
            this.editDialogVisible = false;
        } else {
            if (!definition.constraints) {
                definition.constraints = [];
            }
            this.editDialogMode = "edit";
            this.editDialogVisible = true;
        }
    }

    setNewDefinitionDialogVisible(visible: boolean) {
        this.selectedDefinition = {
            id: "",
            name: "",
            projectId: this.selectedProjectId,
            actionType: "",
            isClientSide: false,
            isAttachment: false,
            isConfirmationRequired: false,
            fields: [],
            constraints: [],
            position: this.actionDefinitions.length,
            positionUpdateDate: null,
            permissions: { admin: false, roles: [] },
            payloadStructure: undefined,
        };
        this.editDialogMode = "create";
        this.editDialogVisible = visible;
        if (!visible) {
            this.clearAiApplications();
        }
    }

    setSearchString(searchString: string) {
        this.searchString = searchString;
    }

    setActionRolesDialogVisible(visible: boolean) {
        this.actionRolesDialogVisible = visible;
    }

    setEditInputDialogVisible(
        visible: boolean,
        editorConfig?: EditorConfig,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange?: (values: any) => void,
    ) {
        this.selectedEditorConfig = visible ? editorConfig : undefined;
        this.editorConfigChangeCallback = visible ? onChange : undefined;
        this.editInputDialogVisible = visible;
    }

    setEditPayloadDialogVisible(
        visible: boolean,
        payloadStructure: PayloadStructure | null,
        onChange?: (values: PayloadStructure | null) => void,
        fields?: string[],
    ) {
        this.selectedPayloadStructure = visible ? payloadStructure : null;
        this.payloadStructureChangeCallback = visible ? onChange : undefined;
        this.payloadStructureFields = visible ? fields : undefined;
        this.editPayloadDialogVisible = visible;
    }

    async loadActionTypes() {
        const res = await this.actionService.getActionTypes();
        res.map((data) => {
            runInAction(() => {
                this.actionTypes = data;
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }).mapErr((err: any) => {
            this.errorStore.addError(err.data);
        });
    }

    async loadDefinitions(projectId: string) {
        if (!projectId) {
            this.isLoading = false;
            return;
        }

        try {
            runInAction(() => {
                this.isLoading = true;
            });

            const response =
                await this.actionService.getActionDefinitions(projectId);

            response
                .map((definitions: ActionDefinition[]) => {
                    runInAction(() => {
                        this.actionDefinitions = definitions!;
                    });
                })
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .mapErr((err: any) => this.errorStore.addError(err.data));
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    async ensureAiProjectsLoaded() {
        if (this.aiProjectsLoaded || this.aiProjectsLoading) {
            return;
        }

        this.setAiProjectsLoading(true);
        const response = await this.actionService.getAiProjects();

        response
            .map((projects) => {
                runInAction(() => {
                    this.aiProjects = projects;
                    this.aiProjectsLoaded = true;
                });
            })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .mapErr((err: any) => this.errorStore.addError(err.data));

        runInAction(() => {
            this.aiProjectsLoading = false;
        });
    }

    async loadAiApplications(projectId?: string) {
        if (!projectId) {
            this.clearAiApplications();
            return;
        }

        if (
            this.aiApplicationsProjectId === projectId &&
            !this.aiApplicationsLoading
        ) {
            return;
        }

        this.setAiApplicationsLoading(true);
        const response = await this.actionService.getAiApplications(projectId);

        response
            .map((applications) => {
                runInAction(() => {
                    this.aiApplications = applications;
                    this.aiApplicationsProjectId = projectId;
                });
            })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .mapErr((err: any) => {
                runInAction(() => {
                    this.aiApplications = [];
                    this.aiApplicationsProjectId = undefined;
                });
                this.errorStore.addError(err.data);
            });

        runInAction(() => {
            this.aiApplicationsLoading = false;
        });
    }

    async openActionRolesDialog() {
        this.setActionRolesDialogVisible(true);
        await this.loadActionRoles(this.selectedProjectId);
    }

    async loadActionRoles(projectId?: string) {
        if (!projectId) {
            runInAction(() => {
                this.actionRoles = [];
            });
            return;
        }

        runInAction(() => {
            this.actionRolesLoading = true;
        });

        try {
            const roles = await this.adminService.getActionRoles(projectId);

            runInAction(() => {
                this.actionRoles = roles;
            });
        } catch (err) {
            this.errorStore.addBasicError(err);
        } finally {
            runInAction(() => {
                this.actionRolesLoading = false;
            });
        }
    }

    async addActionRole(role: { name: string; description?: string }) {
        const normalizedRole = role.name.trim();
        const normalizedDescription = role.description?.trim() ?? "";

        if (!normalizedRole) {
            message.error("Role name is required");
            return false;
        }

        const hasDuplicate = this.actionRoles.some(
            (role) => role.name.toLowerCase() === normalizedRole.toLowerCase(),
        );

        if (hasDuplicate) {
            message.error("Role with the same name already exists");
            return false;
        }

        if (!this.selectedProjectId) {
            message.error("Please select a project first");
            return false;
        }

        runInAction(() => {
            this.actionRolesLoading = true;
        });

        try {
            const response = await this.adminService.createActionRole(
                this.selectedProjectId,
                normalizedRole,
                normalizedDescription,
            );

            if (response.status !== 201) {
                message.error("Error during action role creation");
                return false;
            }

            await this.loadActionRoles(this.selectedProjectId);
            message.success("Action role has been created");
            return true;
        } catch (err) {
            this.errorStore.addBasicError(err);
            return false;
        } finally {
            runInAction(() => {
                this.actionRolesLoading = false;
            });
        }
    }

    async renameActionRole(
        actionRoleId: string,
        currentName: string,
        nextName: string,
        description?: string,
    ) {
        const normalizedNextName = nextName.trim();
        const normalizedDescription = description?.trim() ?? "";

        if (!normalizedNextName) {
            message.error("Role name is required");
            return false;
        }

        if (
            currentName.toLowerCase() !== normalizedNextName.toLowerCase() &&
            this.actionRoles.some(
                (role) =>
                    role.name.toLowerCase() ===
                    normalizedNextName.toLowerCase(),
            )
        ) {
            message.error("Role with the same name already exists");
            return false;
        }

        runInAction(() => {
            this.actionRolesLoading = true;
        });

        try {
            const response = await this.adminService.updateActionRole(
                actionRoleId,
                normalizedNextName,
                normalizedDescription,
            );

            if (response.status !== 200) {
                message.error("Error during action role update");
                return false;
            }

            await Promise.all([
                this.loadDefinitions(this.selectedProjectId),
                this.loadActionRoles(this.selectedProjectId),
            ]);

            message.success("Action role has been updated");
            return true;
        } catch (err) {
            this.errorStore.addBasicError(err);
            return false;
        } finally {
            runInAction(() => {
                this.actionRolesLoading = false;
            });
        }
    }

    async deleteActionRole(actionRoleId: string, roleName: string) {
        const roleSummary = this.actionRoleSummaries.find(
            (role) => role.name === roleName,
        );

        if ((roleSummary?.usedInActions ?? 0) > 0) {
            message.error(
                "Roles that are used in one or more actions cannot be deleted",
            );
            return false;
        }

        runInAction(() => {
            this.actionRolesLoading = true;
        });

        try {
            const response =
                await this.adminService.deleteActionRole(actionRoleId);

            if (response.status !== 200) {
                message.error("Error during action role deletion");
                return false;
            }

            await this.loadActionRoles(this.selectedProjectId);
            message.success("Action role has been deleted");
            return true;
        } catch (err) {
            this.errorStore.addBasicError(err);
            return false;
        } finally {
            runInAction(() => {
                this.actionRolesLoading = false;
            });
        }
    }

    async createActionDefinition(formValues: FormData) {
        try {
            const resp =
                await this.actionService.createActionDefinition(formValues);
            resp.map(() => {
                runInAction(() => {
                    this.loadDefinitions(this.selectedProjectId);
                    this.setNewDefinitionDialogVisible(false);
                });
                this.taskRootStore.loadActionDefinitions();
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            }).mapErr((err: any) => {
                this.errorStore.addError(err.data);
            });
        } catch (err) {
            this.errorStore.addBasicError(err);
        }
    }

    async updateActionDefinition(
        actionType: string,
        name: string,
        title: string,
        fields: ActionField[] | [],
        constraints: ActionConstraint[] | [],
        isConfirmationRequired: boolean,
        permissions?: ActionExecutionPermissions,
        payloadStructure?: PayloadStructure,
    ) {
        const actionDefinitionId = this.selectedDefinition!.id;

        const response = await this.actionService.updateActionDefinition(
            actionDefinitionId,
            actionType,
            name,
            title,
            fields,
            constraints,
            isConfirmationRequired,
            permissions,
            payloadStructure,
        );
        response
            .map(() => {
                runInAction(() => {
                    this.loadDefinitions(this.selectedProjectId);
                    this.setNewDefinitionDialogVisible(false);
                });
                this.taskRootStore.loadActionDefinitions();
            })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .mapErr((err: any) => this.errorStore.addError(err.data));
    }

    async deleteActionDefinition(actionDefinitionId: string) {
        const response =
            await this.actionService.deleteActionDefinition(actionDefinitionId);
        response
            .map(() => {
                runInAction(() => {
                    let newDefinitions = this.actionDefinitions.slice();
                    newDefinitions = newDefinitions.filter(
                        (t) => t.id !== actionDefinitionId,
                    );
                    this.actionDefinitions = newDefinitions;
                });
                this.taskRootStore.loadActionDefinitions();
            })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .mapErr((err: any) => {
                if (err.status === 409) {
                    message.error(err.data!.title);
                } else {
                    this.errorStore.addError(err.data);
                }
            });
    }

    async exportActionDefinition(id: string) {
        await this.actionService.exportActionDefinition(id);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async importActionDefinition(formValues: any) {
        const formData = new FormData();
        formData.append("file", formValues.file);
        formData.append("name", formValues.name);
        if (formValues.title) {
            formData.append("title", formValues.title);
        }
        const result = await this.actionService.importActionDefinition(
            formData,
            this.selectedProjectId,
        );
        result
            .map(() => {
                this.loadDefinitions(this.selectedProjectId);
            })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .mapErr((err: any) => {
                this.errorStore.addError(err.data);
            });
        return result;
    }

    async updateListOrder(id: string, dragIndex: number, dropIndex: number) {
        this.isLoading = true;
        const actionDef = this.actionDefinitions.find((m) => m.id === id)!;
        const resp = await this.actionService.updateActionsListOrder(
            id,
            dragIndex,
            dropIndex,
            actionDef.positionUpdateDate,
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        resp.mapErr((err: any) => {
            this.errorStore.addError(err.data);
        });
        await this.loadDefinitions(this.selectedProjectId);
        runInAction(() => (this.isLoading = false));
    }
}
